<template>
  <div id="patientExamViewPage">
    <div class="container" v-if="exam != null">
      <div class="card shadow-sm border-0 mt-sm-5">
        <div class="card-body">
          <h5 class="card-title">{{ $t('GeneralInformations') }}</h5>

          <div class="row" v-if="exam.exam">
            <div class="col-12 mb-2 col-sm-4">
              <div class="text-muted">{{ $t('Exam') }}</div>
              <div>{{exam.exam.title}}</div>
            </div>
            <div class="col col-12 mb-2 col-sm-4">
              <div class="text-muted">{{ $t('RealizedAt') }}</div>
              <div>{{exam.exam.exam_date}}</div>
            </div>

            <div class="col col-12 mb-2 col-sm-4">
              <div class="text-muted">{{ $t('Order') }}</div>
              <div>{{exam.exam.order_no}}</div>
            </div>

            <div class="col col-12 mb-2 col-sm-4">
              <div class="text-muted">{{ $t('Patient') }}</div>
              <div>{{exam.exam.patient_name}}</div>
            </div>

            <div class="col col-12 mb-2 col-sm-4">
              <div class="text-muted">{{ $t('Location') }}</div>
              <div>{{exam.exam.clinic_name}}</div>
            </div>
          </div>
        </div>

        <div class="dropdown-divider" v-if="exam.study.num_images > 0"></div>

        <div class="card-body" v-if="exam.study.num_images > 0">
          <h5 class="card-title">{{ $t('Images', {count: exam.study.num_images}) }}</h5>

          <div class="alert alert-info" v-if="exam.study.num_images == 0">{{ $t('NoImagesFound') }}</div>
          <p>{{exam.viewingType}}</p>
          <div class="viewer" v-if="exam.study.num_images > 0 && exam.viewing_type == 'dicom'">
                <div class="download-images" v-show="exam.study.viewer">
                  <p>&nbsp;</p>
                  <button
                    class="btn btn-sm btn-secondary"
                    target="_blank"
                    @click="openViewer"
                  >{{ $t("OpenViewer") }}</button>
                </div>
            <img-viewer :series="exam.images.series" />
          </div>

          <div
            class="d-flex flex-row align-items-center flex-wrap"
            v-viewer
            v-if="exam.study.num_images > 0 && exam.viewing_type == 'gallery'"
          >
            <div
              class="image-preview m-sm-2"
              v-for="image in exam.study.images"
              v-bind:key="image.id"
            >
              <img v-bind:src="image" @error="hideImage(image)" />
              <app-loader />
            </div>
          </div>
        </div>

        <div class="dropdown-divider" v-if="exam.reports.length > 0"></div>
        <div class="card-body">
          <div class="download-images" v-show="exam.study.download_url && !confirmMessage">
            <p>{{ $t('DownloadCompleteExam') }}</p>
            <button
              class="btn btn-sm btn-secondary"
              target="_blank"
              @click="confirmMessage = true"
            >{{ $t('Download') }}</button>
          </div>

          <div class="confirm-download" v-show="confirmMessage">
            <p>{{ $t('AreYouSureWantToDownloadFullExam') }}</p>
            <button
              @click="openDownload(exam.study.download_url)"
              class="btn btn-sm btn-secondary"
              target="_blank"
            >{{ $t('DownloadAnyway') }}</button>
          </div>
        </div>

        <div class="dropdown-divider" v-if="exam.reports.length > 0"></div>

        <div class="card-body" v-if="exam.reports.length > 0">
          <h5 class="card-title">{{ $t('Report', {count: exam.reports.length}) }}</h5>

          <div class="alert alert-info" v-if="exam.reports.length == 0">{{ $t('NoReportsAvailable') }}</div>

          <div v-if="exam.reports.length > 0">
            <div
              class="d-flex layout-sm-row layout-column align-items-center"
              v-for="(report, index) in exam.reports"
              v-if="exam.reports"
              v-bind:key="report.id"
            >
              <div class="p-2 flex-fill">{{report.name || `${$t('Report')} #${index+1}`}}</div>
              <div class="d-none p-2 d-sm-block">PDF</div>
                <button
                  @click="openDownload(report.pdf)"
                  class="btn btn-sm btn-secondary"
                  target="_blank"
                >{{ $t('Download') }}</button>
            </div>
          </div>
        </div>

        <div class="dropdown-divider" v-if="exam.attachments.length > 0"></div>

        <div class="card-body" v-if="exam.attachments.length > 0">
          <h5 class="card-title">{{ $t('Attachments') }}</h5>

          <div class="alert alert-info" v-if="exam.attachments.length == 0">{{ $t('NoAttchmentsAvailable') }}</div>

          <div
            class="d-flex layout-sm-row layout-column align-items-center"
            v-for="(attach, index) in exam.attachments"
            v-if="exam.attachments.length > 0"
            v-bind:key="attach.id"
          >
            <div class="flex-fill p-2 text-truncate">{{attach.name || `${$t('Attchment')} #${index+1}`}}</div>
            <div class="d-sm-block p-2 d-none">{{attach.name.split('.')[1]}}</div>
            <div class="text-right p-2">
              <a v-bind:href="attach.link" class="btn btn-sm btn-secondary" target="_blank">
                <span v-if="attach.name.split('.')[1] == 'stl'">{{ $t('View') }}</span>
                <span v-if="attach.name.split('.')[1] != 'stl'">{{ $t('Download') }}</span>
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div class="container mt-5" v-if="exam == null">
      <div
        class="alert alert-danger"
      >{{ $t('ErrorWhileLoadingTryAgain')}}</div>
    </div>
  </div>
</template>

<script>
import { Patient } from "@/services";
import imgViewer from "../../components/imageViewer";

export default {
  name: "patientExamViewPage",
  components: {
    imgViewer
  },
  data() {
    return {
      confirmMessage: false,
      exam: {
        study: {
          num_images: 0
        },
        attachments: [],
        reports: [],
        images: {
          series: []
        },
        viewing_type: ""
      }
    };
  },
  beforeCreate() {
    let myId = this.$route.params.examId;
    
    Patient.getExam(myId)
      .then(response => {
        this.exam = response.data;
        console.log(this.exam)
      })
      .catch(error => {
        this.exam = null;
      });
    // Patient.getImages()
    // 	.then
  },

  methods: {
    hideImage: function(_image) {
      let index = this.exam.study.images.indexOf(_image);
      if (index > -1) this.exam.study.images.splice(index, 1);
    },
    openViewer: function(){
      window.open(this.exam.study.viewer,"_blank")
    },
    openDownload: function(url){
      var link=document.createElement('a');
      let newUrl = encodeURI(url)
      let base64 = btoa(newUrl)
      let encodedUrl = "https://central-api.image2doc.com.br/mobile/get_content_central?data="+base64
      link.href = encodedUrl;
      link.download = encodedUrl;
      link.click();
    }
  }
};
</script>

<style lang="scss">
@import "../../assets/_vars";

.download-images {
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  p {
    margin: 0;
  }
}
.confirm-download {
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-direction: column;
}
.viewer {
  width: 100%;
}
.image-preview {
  width: calc(20% - 1rem);
  height: 128px;
  position: relative;
  overflow: hidden;
  background: #111;

  @media screen and (max-width: $mobile) {
    width: calc(50% - 0.2rem) !important;
    margin: 0.1rem;
  }

  .Loader {
    position: absolute !important;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    margin: auto;
    z-index: 0;
  }

  img {
    width: 100%;
    display: block;
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    margin: auto;
    z-index: 10;
  }
}
</style>



// WEBPACK FOOTER //
// src/pages/patient/patientExamViewPage.vue